// ATTRIBUTION CONFOUND HONESTY.
//
// A counterfactual attribution (attribution-engine.ts) computes actual −
// do-nothing over an attribution window. That number is only clean if the
// committed action was the ONLY thing acting on the metric across the window. In
// reality windows overlap: two levers both hit SG&A, or a freight-regime shift
// moves the metric independently of any decision. When that happens, honest
// attribution must DECLARE the confounds and name the clean sub-window rather than
// present a single false-precision number over a period it cannot cleanly own.
//
// This layers on top of the base AttributedOutcome. It never fabricates or
// smooths the band (that would be the opposite of honesty); it annotates the
// outcome with the overlapping events, computes the largest confound-free
// sub-window, and downgrades the status to 'confounded' so the rollup
// (attribution-ledger rollup) holds confounded outcomes on a separate,
// never-summed line.

import type {
  AttributedOutcome,
  AttributionWindow,
  ConfoundDeclaration,
} from './attribution-engine';

export const CONFOUND_EVENT_SCHEMA_VERSION = 'attribution_confound_event_v1' as const;
export type ConfoundEventSchemaVersion = typeof CONFOUND_EVENT_SCHEMA_VERSION;

export type ConfoundKind = 'overlapping_action' | 'regime_shift' | 'external_condition';

// A candidate confounding event. `overlapping_action` = another committed
// action/lever moving the same metric; `regime_shift` = a market/operating regime
// change (e.g. a freight-rate regime); `external_condition` = an exogenous driver.
export type ConfoundEvent = Readonly<{
  schemaVersion?: ConfoundEventSchemaVersion;
  eventId: string;
  label: string;
  kind: ConfoundKind;
  window: AttributionWindow;
  affectedMetricKeys: readonly string[];
  evidenceRefIds: readonly string[];
}>;

export type ConfoundDetectionResult = Readonly<{
  isConfounded: boolean;
  confounds: readonly ConfoundDeclaration[];
  // The largest sub-interval of the attribution window with no overlapping
  // confound. null when the window is fully confounded.
  cleanWindow: AttributionWindow | null;
}>;

// Detects which candidate events confound an attribution over a window+metric.
// An event confounds iff it touches the metric AND its window overlaps.
export function detectAttributionConfounds(input: {
  attributionWindow: AttributionWindow;
  metricKey: string;
  candidateEvents: readonly ConfoundEvent[];
}): ConfoundDetectionResult {
  const window = input.attributionWindow;
  if (!isValidWindow(window)) {
    return Object.freeze({ isConfounded: false, confounds: Object.freeze([]), cleanWindow: null });
  }

  const overlaps: Array<{ declaration: ConfoundDeclaration; from: string; to: string }> = [];
  for (const event of input.candidateEvents ?? []) {
    if (!event || !isValidWindow(event.window)) continue;
    if (!Array.isArray(event.affectedMetricKeys)) continue;
    if (!event.affectedMetricKeys.includes(input.metricKey)) continue;

    const overlapFrom = maxStr(window.from, event.window.from);
    const overlapTo = minStr(window.to, event.window.to);
    if (overlapFrom > overlapTo) continue; // no overlap

    overlaps.push({
      from: overlapFrom,
      to: overlapTo,
      declaration: Object.freeze({
        eventId: event.eventId,
        label: event.label,
        kind: event.kind,
        overlapWindow: Object.freeze({ from: overlapFrom, to: overlapTo }),
        evidenceRefIds: dedupe(event.evidenceRefIds),
      }),
    });
  }

  if (overlaps.length === 0) {
    return Object.freeze({
      isConfounded: false,
      confounds: Object.freeze([]),
      cleanWindow: Object.freeze({ from: window.from, to: window.to }),
    });
  }

  // Sort declarations deterministically by overlap start, then eventId.
  overlaps.sort((a, b) => (a.from === b.from ? cmp(a.declaration.eventId, b.declaration.eventId) : cmp(a.from, b.from)));

  const cleanWindow = largestFreeSubWindow(window, overlaps.map((o) => ({ from: o.from, to: o.to })));

  return Object.freeze({
    isConfounded: true,
    confounds: Object.freeze(overlaps.map((o) => o.declaration)),
    cleanWindow,
  });
}

// Applies confound honesty to a computed attribution. Returns a new outcome that,
// when confounded, carries the declared confounds, the clean window, a
// 'confounded' status, and a verdict that names the confounds instead of asserting
// clean precision. The numeric band is preserved verbatim — honesty annotates, it
// does not fabricate.
export function applyConfoundHonesty(
  outcome: AttributedOutcome,
  candidateEvents: readonly ConfoundEvent[],
): AttributedOutcome {
  if (outcome.attributionStatus === 'gap') return outcome;

  const detection = detectAttributionConfounds({
    attributionWindow: outcome.attributionWindow,
    metricKey: outcome.metricKey,
    candidateEvents,
  });

  if (!detection.isConfounded) {
    return Object.freeze({
      ...outcome,
      confounds: Object.freeze([]),
      cleanWindow: detection.cleanWindow,
      attributionStatus: 'attributed',
      verdictSentence: outcome.verdictSentence,
    });
  }

  return Object.freeze({
    ...outcome,
    confounds: detection.confounds,
    cleanWindow: detection.cleanWindow,
    attributionStatus: 'confounded',
    verdictSentence: buildConfoundedVerdict(outcome, detection),
  });
}

function buildConfoundedVerdict(
  outcome: AttributedOutcome,
  detection: ConfoundDetectionResult,
): string {
  const names = detection.confounds.map((c) => `${c.label} (${c.kind})`).join(', ');
  const clean = detection.cleanWindow
    ? `the clean window is ${detection.cleanWindow.from} to ${detection.cleanWindow.to}`
    : 'there is no confound-free sub-window';
  const dollars = (usd: number): string => `${usd < 0 ? '-' : ''}$${Math.abs(usd).toLocaleString('en-US')}`;
  return (
    `Attribution over ${outcome.attributionWindow.from} to ${outcome.attributionWindow.to} is ` +
    `confounded by ${names}; ${clean}. Attributed impact is reported only as a range ` +
    `(${dollars(outcome.attributedImpact.conservativeUsd)} to ` +
    `${dollars(outcome.attributedImpact.stretchUsd)}) — no single figure is asserted over the ` +
    `confounded window.`
  );
}

// The largest confound-free contiguous sub-interval of `window` after removing the
// union of `intervals`. Returns null when the window is fully covered.
function largestFreeSubWindow(
  window: AttributionWindow,
  intervals: ReadonlyArray<{ from: string; to: string }>,
): AttributionWindow | null {
  const sorted = [...intervals]
    .map((iv) => ({ from: maxStr(iv.from, window.from), to: minStr(iv.to, window.to) }))
    .filter((iv) => iv.from <= iv.to)
    .sort((a, b) => (a.from === b.from ? cmp(a.to, b.to) : cmp(a.from, b.from)));

  let cursor = window.from;
  let best: AttributionWindow | null = null;
  const consider = (from: string, to: string): void => {
    if (from < to && (best === null || diff(from, to) > diff(best.from, best.to))) {
      best = Object.freeze({ from, to });
    }
  };

  for (const iv of sorted) {
    if (iv.from > cursor) consider(cursor, iv.from);
    if (iv.to > cursor) cursor = iv.to;
  }
  if (cursor < window.to) consider(cursor, window.to);

  return best;
}

function isValidWindow(window: unknown): window is AttributionWindow {
  return (
    typeof window === 'object' &&
    window !== null &&
    typeof (window as AttributionWindow).from === 'string' &&
    typeof (window as AttributionWindow).to === 'string' &&
    (window as AttributionWindow).from <= (window as AttributionWindow).to
  );
}

function maxStr(a: string, b: string): string {
  return a >= b ? a : b;
}

function minStr(a: string, b: string): string {
  return a <= b ? a : b;
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// Ordinal day difference between two ISO date strings (approximate span in days;
// used only to rank sub-window sizes, so exact calendar precision is unnecessary).
function diff(from: string, to: string): number {
  const f = Date.parse(from);
  const t = Date.parse(to);
  if (Number.isFinite(f) && Number.isFinite(t)) return t - f;
  return cmp(to, from);
}

function dedupe(values: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values ?? []) {
    if (typeof value === 'string' && value.length > 0 && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return Object.freeze(out);
}
